<template>
    <div class="tw-flex tw-flex-col level-picker">
        <p class="form-card-description tw-text-sm tw--mt-3 xxl:tw-text-normal" v-if="display_explanation" v-html="'Levels are used to determine what a user has access to.'"></p>
        <slot></slot>
        <template v-if="levels_loaded">
            <template v-if="levels.length > 0">
                <div class="tw-flex tw-justify-end tw-text-sm secondary-c tw-mb-2 tw-select-none xxl:tw-text-normal">
                    <p class="hover-opacity" v-text="activeLevelIds.length !== levels.length ? 'Select all levels' : 'Deselect all levels'" @click="toggleAll"></p>
                </div>
                <div class="levels-filter tw-relative tw-mb-3">
                    <i class="search-icon far fa-search tw-absolute tw-text-base extra-light-text"></i>
                    <form-row>
                        <form-field>
                            <form-input v-model="search_query" placeholder="Search through levels" @keydown="searchLevels"></form-input>
                        </form-field>
                    </form-row>
                    <i class="clear-icon far fa-times tw-absolute extra-light-text hover-opacity" v-if="search_query !== ''" @click="clearSearch"></i>
                </div>
                <template v-if="visibleLevels.length > 0">
                    <transition-group name="order-levels" tag="div" class="level-wrapper md:tw-grid md:tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2">
                        <div class="level tw-flex tw-items-center tw-justify-between tw-rounded tw-px-4 tw-py-3 tw-cursor-pointer tw-mb-2 md:tw-mb-0" v-for="level in visibleLevels" :key="level.id" @click="toggleLevel(level.id)" :class="{'active': activeLevelIds.includes(level.id) || lockedLevelIds.includes(level.id)}">
                            <div class="tw-flex tw-flex-col tw-mr-4">
                                <p class="tw-pr-1 tw-flex-1" v-text="level.name"></p>
                                <span class="tw-text-xs tw-uppercase tw-font-semibold tw-mt-1 light-text" v-if="level.default" v-text="'Default'"></span>
                            </div>
                            <transition name="fade">
                                <div class="tw-flex tw-items-center tw-justify-center tw-text-center tw-rounded-full tw-font-semibold tw--mr-px tw-text-gray-400 tw-text-xs" v-if="transitioning_levels && !activeLevelIds.includes(level.id) && !lockedLevelIds.includes(level.id) && visibleLevels.length > 1"><span class="-ml-px -mt-px">{{ level.name.charAt(0).toUpperCase() }}</span></div>s
                            </transition>
                            <i class="fal fa-check tw-hidden" v-if="!lockedLevelIds.includes(level.id)"></i>
                            <i class="fal fa-lock tw-hidden" v-else></i>
                        </div>
                    </transition-group>
                    <div class="tw-rounded tw-p-3 tw-bg-gray-0 tw-text-sm tw-mt-3 tw-flex" v-if="display_no_levels_message">
                        <i class="fal fa-question-circle tw-mr-1 tw-mt-1"></i>
                        When no level is selected then all users have access.
                    </div>
                </template>
                <p v-else class="tw-mb-2" v-text="'No levels found.'"></p>
            </template>
            <template v-else>
                <div class="tw-flex tw-flex-col" :class="{'opacity-50 pointer-events-none': refreshing_levels}">
                        <p class="tw-mb-2" v-text="'No levels found.'"></p>
                    <div class="tw-inline-flex">
                        <div class="primary-button btn-v2 slim tw-text-sm" @mousedown="openLevelsPage">
                            <i class="far fa-plus"></i>
                            <p v-text="'New level'"></p>
                        </div>
                    </div>
                </div>
                <div class="tw-inline-flex tw-mt-3" @click="refreshLevels" v-if="allow_refresh">
                    <div class="subtle-button slim tw-text-sm">
                        <i class="fal fa-sync"></i>
                        <p v-text="'Refresh levels'"></p>
                    </div>
                </div>
            </template>
        </template>
        <div class="tw-inline-flex" v-else>
            <i class="far fa-spinner fa-spin light-text"></i>
        </div>
    </div>
</template>

<script>
    import orderBy from 'lodash.orderby';
    import Levels from '../../mixins/v2/Levels';

    export default {
        name: 'LevelsList',

        mixins: [Levels],

        props: {
            value: {
                /* default() {
                    return [];
                }, */
                required: true,
            },
            guardable_id: {},
            guardable_type: {
                type: String,
            },
            display_explanation: {
                default: true,
            },
            needle: {
                default: 'level_id',
            },
            locked: {
                default: false,
            },
            locked_message: {
                default: '',
            },
            toggle_default: {
                default: false,
            },
            display_no_levels_message: {
                default: true,
            },
        },

        data: () => ({
            active_levels: [],
            locked_levels: [],
            search_query: '',
            search_results: [],
            transitioning_levels: false,
        }),

        computed: {
            levelsType() {
                return typeof this.value === 'string' ? 'string' : 'array';
            },

            activeLevelIds() {
                if (this.levelsType === 'string') {
                    // return an array of all ids but parsed as a number
                    return this.active_levels.map(Number);
                }

                return this.active_levels.map(level => level[this.needle]);
            },

            lockedLevelIds() {
                if (this.levelsType === 'string') {
                    // return an array of all ids but parsed as a number
                    return this.locked_levels.map(Number);
                }

                return this.locked_levels.map(level => level[this.needle]);
            },

            visibleLevels() {
                let results = [];

                if (this.search_query !== '') {
                    results = this.search_results;
                } else {
                    results = this.levels;
                }

                return orderBy(results, [level => this.activeLevelIds.includes(level.id), level => level.name.toLowerCase()], ['desc', 'asc']);
            },
        },

        methods: {
            toggleAll() {
                let force_enable = false;

                if (this.activeLevelIds.length !== this.levels.length) {
                    force_enable = true;
                }

                for (let i = 0; i < this.levels.length; i += 1) {
                    this.toggleLevel(this.levels[i].id, force_enable);
                }
            },

            toggleLevel(level_id, force_enable = false) {
                if (!this.lockedLevelIds.includes(level_id)) {
                    if (this.activeLevelIds.includes(level_id)) {
                        if (!force_enable) {
                            if (this.levelsType === 'string') {
                                const active_level_index = this.active_levels.findIndex(level => parseInt(level) === level_id);

                                this.active_levels.splice(active_level_index, 1);
                            } else {
                                const active_level_index = this.active_levels.findIndex(level => level[this.needle] === level_id);

                                this.active_levels.splice(active_level_index, 1);
                            }
                        }
                    } else {
                        // eslint-disable-next-line no-lonely-if
                        if (this.levelsType === 'string') {
                            this.active_levels.push(level_id);
                        } else {
                            const new_level = {
                                guardable_id: this.guardable_id,
                                [this.needle]: level_id,
                            };

                            if (typeof this.guardable_type !== 'undefined') {
                                new_level.guardable_type = this.guardable_type;
                            }

                            this.active_levels.push(new_level);
                        }
                    }

                    if (this.levelsType === 'string') {
                        // change the array of levels back into a string
                        this.$emit('input', this.active_levels.join(','));
                    } else {
                        this.$emit('input', this.active_levels);
                    }

                    this.transitioning_levels = true;

                    setTimeout(() => {
                        this.transitioning_levels = false;
                    }, 1000);
                }
            },

            setLocalLevels() {
                this.levelsToArray();

                if (this.locked !== false) {
                    this.levelsToArray('locked', 'locked_levels');
                }
            },

            levelsToArray(data_field = 'value', active_data_field = 'active_levels') {
                if (this.levelsType === 'string') {
                    // make the string into an array by splitting it on each ','
                    this[active_data_field] = this[data_field] ? this[data_field].split(/\s*,\s*/) : [];
                } else {
                    this[active_data_field] = this[data_field] ? this[data_field] : [];
                }
            },

            searchLevels() {
                this.search_results = [];

                for (let i = 0; i < this.levels.length; i += 1) {
                    if (this.levels[i].name.toLowerCase().indexOf(this.search_query.toLowerCase()) !== -1) {
                        this.search_results.push(this.levels[i]);
                    }
                }
            },

            clearSearch() {
                this.search_query = '';
            },
        },

        mounted() {
            this.setLocalLevels();
        },

        // we need to watch the value because for example a user might already be loaded by the fetch method
        // but the user was loaded without levels during the fetch, so when we open this with the show function
        // it gets mounted immediately but we dont have levels data, so it turns out blank
        // so we 'reset' the levels during each value change so it's always up to date
        watch: {
            value() {
                this.setLocalLevels();
            },

            levels_loaded(loaded) {
                if (loaded === true && this.toggle_default) {
                    for (let i = 0; i < this.levels.length; i += 1) {
                        if (this.levels[i].default === true && !this.lockedLevelIds.includes(this.levels[i].id)) {
                            this.toggleLevel(this.levels[i].id);
                        }
                    }
                }
            },
        },
    };
</script>
